import { TableBody, Button, Box, useTheme } from '@mui/material';
import React, { useEffect, useMemo, useState, useCallback } from 'react';
import { useHits, useInfiniteHits, useInstantSearch } from 'react-instantsearch';
import { useKanbanContext } from '@/context/kanbanContext';
import { useTableSort } from '@/context/tableSortContext';
import { stableSort, getComparator } from '@/components/table/functions';
import { useBatchGetDealsTasksQuery } from '@/features/engagements/engagementsApi';
import { useBatchGetDealListPoliciesQuery } from '@/features/deals/dealsApi';
import { useSelector, useStore } from 'react-redux';
import { usesDealRowCache } from '@/utils/pipelineDealSync';
import { buildRowCacheSnapshot, mergeCachedRow } from '@/utils/rowCacheRegistry';
import GlobalSearchHelper from '@/components/tables/InstantSearchDataGrid/GlobalSearchHelper';

export const HitsTableBody = ({
	TableRowComponent,
	EmptyRowComponent,
	ErrorRowComponent,
	includeTasks = false,
	includePolicies = false,
	headCells, // optional
	order, // optional
	orderBy, // optional
	rowsPerPage, // optional
	objectType,
	isError,
	...props
}) => {
	const { items: hits } = useHits(props);
	const store = useStore();

	const { uiState } = useInstantSearch(props);
	const indexName = Object.keys(uiState)?.[0];

	const [searchItems, setSearchItems] = useState([]);

	const items = useMemo(() => {
		const merged = [...hits, ...searchItems];
		let deduped = merged.filter(
			(item, index, self) => index === self.findIndex((t) => t.id === item.id),
		);

		const sortBy = uiState?.[indexName]?.sortBy;
		let orderBy = null;
		let order = 'asc';

		if (sortBy?.includes('/sort/')) {
			const sortPart = sortBy.split('/sort/')[1];
			const [field, direction] = sortPart.split(':');
			orderBy = field;
			order = direction ?? 'asc';
		}

		if (orderBy) {
			deduped = stableSort(deduped, getComparator(order, orderBy, true));
		}
		if (rowsPerPage) {
			deduped = deduped.slice(0, rowsPerPage);
		}

		return deduped;
	}, [hits, searchItems, uiState?.[indexName]?.sortBy]);

	const dealIds = items.map((h) => h.id);

	// Serialised picked fields so updatedHits recomputes when a cached row changes.
	const rowCacheSnapshot = useSelector((state) =>
		buildRowCacheSnapshot(state, objectType, dealIds),
	);

	const tasksQuery = useBatchGetDealsTasksQuery(
		{ dealIds, nextTaskOnly: true },
		{ skip: dealIds.length === 0 || !includeTasks || !objectType },
	);

	const policiesQuery = useBatchGetDealListPoliciesQuery(
		{ dealIds },
		{ skip: dealIds.length === 0 || !usesDealRowCache(objectType) || !includePolicies },
	);

	const updatedHits = useMemo(() => {
		const state = store.getState();
		const policyData = policiesQuery.data ?? {};
		let newItems = items.map((hit) => {
			const rowId = hit.id;
			const mergedHit = mergeCachedRow(state, objectType, hit, rowId);

			const earliestTask = (tasksQuery.data ?? {})[rowId];

			const policies = policyData?.policiesByDealId?.[mergedHit.dealId] ?? [];
			const policyStatus = policyData?.statusByDealId?.[mergedHit.dealId];

			const policiesLoading =
				policiesQuery.isUninitialized || !policyStatus || policyStatus === 'loading';

			return {
				...mergedHit,
				id: rowId,
				docId: mergedHit.id,
				earliestTask,
				nextTaskDate: mergedHit.nextTaskDate,
				policies,
				...(includePolicies && {
					policiesLoading,
				}),
			};
		});
		const sortCell = (headCells ?? []).find((i) => orderBy && i.id === orderBy);
		if (sortCell?.frontEndSort) {
			newItems = stableSort(newItems, getComparator(order, orderBy, true));
		}
		return newItems;
	}, [
		items,
		objectType,
		rowCacheSnapshot,
		store,
		tasksQuery.data,
		orderBy,
		order,
		headCells,
		policiesQuery.data,
		policiesQuery.isUninitialized,
	]);

	return (
		<>
			<GlobalSearchHelper {...{ setSearchItems }} />
			<TableBody>
				{isError ? (
					<ErrorRowComponent />
				) : items.length > 0 ? (
					updatedHits.map((hit) => <TableRowComponent key={hit.id} hit={hit} />)
				) : (
					<EmptyRowComponent />
				)}
			</TableBody>
		</>
	);
};

export const HitsKanbanColumn = ({
	CardComponent,
	LoadingComponent,
	includeTasks = false,
	includePolicies = false,
	includeTransactions = false,
	objectType,
	stageId,
	onLoadMore,
	...props
}) => {
	const theme = useTheme();
	// Infinite hits gives paginated search results for this kanban column.
	const { items, isLastPage, showMore, results } = useInfiniteHits({
		...props,
	});
	// Total number of matching hits in the search index.
	const total = results.nbHits;

	// Local loading flag used while fetching more cards.
	const [isLoadingAll, setIsLoadingAll] = useState(false);

	// Pull current kanban state and helpers from context.
	const { dealStages, updateDealStages, updateRefreshStatus, refreshStatus } = useKanbanContext();
	const { currentSort } = useTableSort();
	const useIncomingOrder = Boolean(currentSort?.field && currentSort?.sort);

	// The cards currently stored for this stage in shared kanban state.
	const currentStage = dealStages?.[stageId] ?? [];

	// Normalised ids for all items in this column.
	const itemIds = useMemo(() => items.map((h) => h.id), [items]);

	const tasksQuery = useBatchGetDealsTasksQuery(
		{ dealIds: itemIds, nextTaskOnly: true },
		{ skip: itemIds.length === 0 || !includeTasks || !objectType },
	);

	const { data: policyData, ...policiesQuery } = useBatchGetDealListPoliciesQuery(
		{ dealIds: itemIds, includeTransactions },
		{ skip: itemIds.length === 0 || !usesDealRowCache(objectType) || !includePolicies },
	);

	// Enrich each card with normalised ids and task info.
	const updatedHits = useMemo(() => {
		const taskData = tasksQuery.data ?? {};
		const hits = items.map((hit) => {
			const hitId = hit.id;
			const policies = policyData?.policiesByDealId?.[hitId] ?? [];
			const policyStatus = policyData?.statusByDealId?.[hitId];

			const policiesLoading =
				policiesQuery.isUninitialized || !policyStatus || policyStatus === 'loading';

			return {
				...hit,
				id: hitId,
				docId: hit.id,
				earliestTask: taskData[hitId],
				policies: !includePolicies ? [] : policies,
				...(includePolicies && {
					policiesLoading,
				}),
			};
		});

		return hits;
	}, [items, tasksQuery.data, policyData, includePolicies]);

	// Sum the dealAmount values for the visible cards in this stage.
	const sum = useMemo(
		() =>
			updatedHits.reduce((sum, hit) => {
				const amount = parseFloat(hit.dealAmount) || 0;
				return sum + amount;
			}, 0),
		[updatedHits],
	);

	// Notify parent with the current total hit count and summed deal amount.
	useEffect(() => onLoadMore?.(total, sum), [total, sum]);

	// InstantSearch helpers used to reset pagination and refresh the column.
	const { refresh, setUiState } = useInstantSearch();

	// Resets this search back to page 0 and forces a refresh.
	const resetAndRefresh = useCallback(() => {
		updateRefreshStatus(stageId, true);
		setUiState((uiState) => ({
			...uiState,
			instant_search: {
				...uiState.instant_search,
				page: 0,
			},
		}));
		refresh();
	}, [stageId, setUiState, refresh, updateRefreshStatus]);

	// When this stage is marked for refresh in context, reset and reload it.
	useEffect(() => {
		if (refreshStatus?.[stageId]) {
			resetAndRefresh();
		}
	}, [refreshStatus?.[stageId]]);

	// Loads the next page of cards when "Show more" is clicked.
	const handleShowMore = () => {
		setIsLoadingAll(true);
		if (!isLastPage) {
			showMore();
		}
		setIsLoadingAll(false);
	};

	// Push the latest enriched hits into shared kanban context for this stage.
	useEffect(() => {
		updateDealStages(stageId, updatedHits, { useIncomingOrder });
	}, [updatedHits, stageId, updateDealStages, useIncomingOrder]);

	return (
		<>
			{isLoadingAll && (
				<Box sx={{ p: 2, textAlign: 'center' }}>
					<LoadingComponent />
				</Box>
			)}

			{!isLoadingAll &&
				currentStage.map((hit, index) => (
					<CardComponent key={hit.id} hit={hit} index={index} />
				))}

			{!isLastPage && (
				<Box sx={{ p: 1, textAlign: 'center' }}>
					<Button
						variant='text'
						size='small'
						onClick={handleShowMore}
						disabled={isLoadingAll}
						sx={{
							fontSize: '11px',
							textTransform: 'none',
							color: theme.palette.primary.main,
							'&:hover': {
								backgroundColor: theme.palette.action.hover,
							},
						}}
					>
						{isLoadingAll
							? 'Loading...'
							: `+ Show more (${total - updatedHits.length} remaining)`}
					</Button>
				</Box>
			)}
		</>
	);
};
